import React, { useEffect, useState, useCallback } from 'react';
import {
    Typography,
    List,
    ListItem,
    ListItemText,
    Switch,
    TextField,
    MenuItem,
    CardContent,
    CardHeader,
    Box,
} from "@mui/material";
import { Loader } from "../../../../../../Components";
import { fetchSettings, updateSetting } from "../../../Redux/Action";
import { useDispatch } from "react-redux";
import { SettingCard } from "../../../../../../Components/Cards/SettingCard";

// [
//     // General Settings
//     { id: 1, name: "Enable Integration", description: "Enable Microsoft 365 booking integration", type: "switch", value: true, group: "General Settings" },

//     // Application Settings
//     { id: 2, name: "App Token Sync Frequency", description: "Set how often the application syncs tokens.", type: "dropdown", value: "30_minutes", group: "Application Settings", options: [
//             { id: "15_minutes", label: "Every 15 Minutes" },
//             { id: "30_minutes", label: "Every 30 Minutes" },
//             { id: "hourly", label: "Hourly" },
//             { id: "daily", label: "Daily" },
//         ] },
//     { id: 3, name: "Last Successful Sync", description: "Time of the last successful token refresh.", type: "text", value: "2025-08-25 10:30 AM", group: "Application Settings", readOnly: true },

//     // User Settings
//     { id: 4, name: "Auto Sync Bookings", description: "Enable automatic booking synchronization.", type: "switch", value: false, group: "User Settings" },
//     { id: 5, name: "User Authentication URL", description: "URL for user to grant access to their calendar.", type: "text", value: "https://login.microsoftonline.com/your-tenant-id/oauth2/v2.0/authorize?...", group: "User Settings", readOnly: true, typeUrl: true },

// ]


export const Microsoft365Tab = () => {

    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState([]);

    // Fetch settings data
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        const res = await fetchSettings({ dispatch, filters: { paginate: 'false', category: 'integration' } });
        if (res.success) {
            setSettings(res.data);
        }
        setIsLoading(false);
    }, [dispatch]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Handle value change
    const handleValueChange = async (id, newValue, parameterType) => {
        // Update the local state
        setSettings((prevSettings) =>
            prevSettings.map((item) =>
                item.id === id
                    ? {
                          ...item,
                          parameter_value: parameterType === 'JSON' ? null : newValue,
                          parameter_json: parameterType === 'JSON' ? newValue : null,
                      }
                    : item
            )
        );

        // Prepare the payload for the API
        const payload = {
            id: id,
            parameter_value: parameterType === 'JSON' ? null : newValue,
            parameter_json: parameterType === 'JSON' ? newValue : null,
        };

        await updateSetting({ dispatch, data: payload }).then((res) => {
            if (res.success) {
            }
        });
    };

    // Group settings by their group property
    const groupedSettings = settings.reduce((groups, item) => {
        const group = item.ui_group || "General";
        if (!groups[group]) groups[group] = [];
        groups[group].push(item);
        return groups;
    }, {});

    return (
        <SettingCard>
            <CardHeader
                title="Microsoft 365 Integration"
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
            />
            <CardContent>
                {isLoading ? (
                    <Typography variant="caption">Loading...</Typography>
                ) : Object.keys(groupedSettings).length === 0 ? (
                    <Typography variant="caption">No settings found</Typography>
                ) : (
                    Object.entries(groupedSettings).map(([group, groupItems]) => (
                        <Box key={group}>
                            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                                {group}
                            </Typography>
                            <List>
                                {groupItems.map((item) => (
                                    <ListItem key={item.id}>
                                        {/* Setting Name and Description */}
                                        <ListItemText
                                            primary={item.title}
                                            secondary={item.description}
                                        />
                                        {/* Render Input Based on Type */}
                                        {item.type === "switch" && (
                                            <Switch
                                                disabled={!item.is_active}
                                                edge="end"
                                                checked={item.parameter_value === "true"}
                                                onChange={(e) =>
                                                    handleValueChange(item.id, e.target.checked ? "true" : "false")
                                                }
                                                color="primary"
                                                inputProps={{
                                                    "aria-label": `toggle ${item.title}`,
                                                }}
                                            />
                                        )}
                                        {item.type === "text" && (
                                            <TextField
                                                disabled={!item.is_active}
                                                value={item.parameter_value || ""}
                                                onChange={(e) => handleValueChange(item.id, e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                inputProps={{
                                                    "aria-label": `input ${item.title}`,
                                                }}
                                            />
                                        )}
                                        {item.type === "multi-select" && (
                                            <TextField
                                                select
                                                disabled={!item.is_active}
                                                SelectProps={{
                                                    multiple: true,
                                                    value: item.parameter_value
                                                        ? item.parameter_value.split(",")
                                                        : [],
                                                    onChange: (e) =>
                                                        handleValueChange(
                                                            item.id,
                                                            e.target.value.join(","),
                                                            item.parameter_type
                                                        ),
                                                }}
                                                variant="outlined"
                                                size="small"
                                            >
                                                {item.options?.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                        {item.type === "dropdown" && (
                                            <TextField
                                                select
                                                disabled={!item.is_active}
                                                value={item.parameter_value || ""}
                                                onChange={(e) => handleValueChange(item.id, e.target.value)}
                                                variant="outlined"
                                                size="small"
                                            >
                                                {item.options?.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    ))
                )}
            </CardContent>
        </SettingCard>
    );
};